import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'deleteConversationTitle'
	| 'modalBody'
	| 'deleteButtonLabel'
	| 'cancelButtonLabel'
	| 'genericErrorMessage'
	| 'conversationNotFoundErrorMessage';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		deleteConversationTitle: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.delete-conversation-title.non-final',
			defaultMessage: 'Delete Conversation "{name}"?',
			description:
				'Title text displayed at the top of a confirmation modal dialog when deleting a conversation within the AI conversation interface. Shown as the modal dialog header title. The name placeholder will be substituted with the conversation name (e.g., "Project Planning Discussion"). The purpose is to immediately communicate to the user what action they are about to confirm, which is permanently deleting a specific conversation.',
		},
		modalBody: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.modal-body.non-final',
			defaultMessage:
				'Are you sure you want to delete this conversation? This action cannot be undone.',
			description:
				'Warning message text displayed in the body of a confirmation modal dialog when deleting a conversation within the AI conversation interface. Shown as the main text in the modal dialog body. The purpose is to warn users that deletion is permanent and irreversible, and to encourage them to carefully consider before confirming the deletion.',
		},
		deleteButtonLabel: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.delete-button-label.non-final',
			defaultMessage: 'Delete',
			description:
				'Button label displayed on the confirm/delete button in a confirmation modal dialog when deleting a conversation within the AI conversation interface. Shown as the text on a button in the modal dialog. When clicked, it confirms the deletion and permanently removes the conversation. The purpose is to provide a clear way for users to confirm they want to proceed with the irreversible deletion action.',
		},
		cancelButtonLabel: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.cancel-button-label.non-final',
			defaultMessage: 'Cancel',
			description:
				'Button label displayed on the cancel button in a confirmation modal dialog when deleting a conversation within the AI conversation interface. Shown as the text on a button in the modal dialog. When clicked, it closes the confirmation dialog without deleting the conversation. The purpose is to provide users with a way to cancel the deletion action if they change their mind.',
		},
		genericErrorMessage: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.generic-failure-message.non-final',
			defaultMessage: 'An error occurred while deleting the conversation. Please try again.',
			description:
				'Error message displayed in a confirmation modal dialog or notification when a generic error occurs while deleting a conversation within the AI conversation interface. Shown as an error message in the modal or in an error notification. The purpose is to inform the user that the deletion encountered an unexpected error and prompt them to retry the action.',
		},
		conversationNotFoundErrorMessage: {
			id: 'ai-mate.conversation-assistant.conversation-picker.delete-confirm-modal.conversation-not-found-failure-message.non-final',
			defaultMessage: 'The conversation you are trying to delete does not exist.',
			description:
				'Error message displayed in a confirmation modal dialog or notification when attempting to delete a conversation that no longer exists within the AI conversation interface. Shown as an error message in the modal or in an error notification. The purpose is to inform the user that the conversation they are trying to delete has already been deleted or does not exist, possibly due to it being deleted by another user or session.',
		},
	});

export default message;
